import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { Report, REPORT_TYPE_ICON, REPORT_TYPE_LABELS, ReportType } from '../models/report.model';
import { ReportMockService } from '../services/report-mock.service';

@Component({
  selector: 'app-reports-list',
  templateUrl: './reports-list.component.html',
  styleUrls: ['./reports-list.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class ReportsListComponent implements OnInit {
  rows: Report[] = [];
  search = '';
  typeFilter: ReportType | 'all' = 'all';
  sortKey: 'title' | 'ownerOrgName' | 'createdAt' = 'createdAt';
  sortDir: 'asc' | 'desc' = 'desc';
  page = 0;
  pageSize = 10;
  pageSizes = [5, 10, 15, 20, 25];
  types: Array<ReportType | 'all'> = ['all', 'group_profile', 'group_profile_plus', 'team_member_report'];

  private all: Report[] = [];

  constructor(private svc: ReportMockService, private router: Router) {}

  ngOnInit(): void { this.reload(); }

  reload(): void {
    this.svc.list().subscribe(rows => { this.all = rows; this.applyView(); });
  }

  /** Filter + sort + paginate in the component: the whole list is local and small. */
  private applyView(): void {
    let rows = [...this.all];
    if (this.typeFilter !== 'all') { rows = rows.filter(r => r.type === this.typeFilter); }
    const s = this.search.trim().toLowerCase();
    if (s) {
      rows = rows.filter(r =>
        r.title.toLowerCase().includes(s) ||
        r.ownerOrgName.toLowerCase().includes(s) ||
        r.createdBy.toLowerCase().includes(s));
    }
    const dir = this.sortDir === 'asc' ? 1 : -1;
    rows.sort((a, b) => `${a[this.sortKey]}`.localeCompare(`${b[this.sortKey]}`) * dir);
    this.count = rows.length;
    // Clamp page here (not just in setPage()): the list can shrink under the current page
    // (delete, filter, search) via reload()/remove(), which call applyView() directly and
    // bypass setPage()'s guard. Without this, count > 0 hides the empty state, pageCount() === 1
    // hides the pager, and the user is stranded on a blank page with no way back.
    const lastPage = Math.max(0, Math.ceil(this.count / this.pageSize) - 1);
    this.page = Math.min(this.page, lastPage);
    this.rows = rows.slice(this.page * this.pageSize, this.page * this.pageSize + this.pageSize);
  }

  count = 0;

  onFilter(): void { this.page = 0; this.applyView(); }
  clearSearch(): void { this.search = ''; this.onFilter(); }
  setPage(p: number): void { this.page = Math.max(0, Math.min(p, this.pageCount() - 1)); this.applyView(); }
  pageCount(): number { return Math.max(1, Math.ceil(this.count / this.pageSize)); }
  pages(): number[] { return Array.from({ length: this.pageCount() }, (_, i) => i); }
  pageStart(): number { return this.count === 0 ? 0 : this.page * this.pageSize + 1; }
  pageEnd(): number { return Math.min((this.page + 1) * this.pageSize, this.count); }

  sortBy(key: 'title' | 'ownerOrgName' | 'createdAt'): void {
    if (this.sortKey === key) { this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'; }
    else { this.sortKey = key; this.sortDir = 'asc'; }
    this.page = 0;
    this.applyView();
  }
  sortIcon(key: 'title' | 'ownerOrgName' | 'createdAt'): string {
    if (this.sortKey !== key) { return 'fa-sort'; }
    return this.sortDir === 'asc' ? 'fa-sort-asc' : 'fa-sort-desc';
  }

  typeLabel(t: ReportType | 'all'): string { return t === 'all' ? 'All types' : REPORT_TYPE_LABELS[t]; }
  typeIcon(t: ReportType): string { return REPORT_TYPE_ICON[t]; }
  initials(name: string): string { return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase(); }

  create(): void { this.router.navigate(['/reports/new']); }
  edit(r: Report): void { this.router.navigate(['/reports', r.id, 'edit']); }

  /**
   * Viewing a generated report needs the reporting API, which isn't wired yet. Say so rather than
   * routing to the wizard: the row and the title are "open this report", not "edit its definition".
   */
  open(r: Report): void {
    Swal.fire({
      title: r.title,
      text: 'Viewing a report will be available once the reporting API integration is finished.',
      icon: 'info',
      confirmButtonText: 'OK',
      confirmButtonColor: '#69be28',
    });
  }

  /** Clone in place — the copy lands in the listing; edit it from there if it needs changes. */
  duplicate(r: Report, ev: Event): void {
    ev.stopPropagation();
    this.svc.duplicate(r.id).subscribe(copy => {
      this.reload();
      Swal.fire({
        title: 'Report cloned', text: `Created "${copy.title}".`, icon: 'success',
        timer: 1600, showConfirmButton: false,
      });
    });
  }

  remove(r: Report, ev: Event): void {
    ev.stopPropagation();
    Swal.fire({
      title: `Delete "${r.title}"?`, text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonText: 'Delete', confirmButtonColor: '#f54359',
    }).then(res => { if (res.isConfirmed) { this.svc.remove(r.id).subscribe(() => this.reload()); } });
  }
}
